import { FC, useCallback, useMemo } from 'react';
import { Calendar, Clock, Trash2, User } from 'lucide-react';
import { cn } from '@new/lib';
import { useDeleteAvailabilitySlotMutation, type UserAvailabilitySlot } from '@teachur/http-client';
import { formatSlotTime, getSlotStatus } from '../../lib/helpers';

interface AvailabilitySlotListProps {
  slots: UserAvailabilitySlot[];
  title?: string;
}

export const AvailabilitySlotList: FC<AvailabilitySlotListProps> = ({
  slots,
  title = 'Your Availability Slots',
}) => {
  const deleteSlot = useDeleteAvailabilitySlotMutation();

  const handleDelete = useCallback(
    (slotId: number) => {
      deleteSlot.mutate(slotId);
    },
    [deleteSlot]
  );

  const { availableSlots, pendingSlots, bookedSlots } = useMemo(() => {
    return {
      availableSlots: slots.filter((slot) => getSlotStatus(slot) === 'available'),
      pendingSlots: slots.filter((slot) => getSlotStatus(slot) === 'pending'),
      bookedSlots: slots.filter((slot) => getSlotStatus(slot) === 'accepted'),
    };
  }, [slots]);

  if (slots.length === 0) {
    return (
      <div className="bg-(--neutral-white-NW1000) rounded-xl border border-neutral-200 p-8">
        <div className="text-center">
          <Calendar className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
          <p className="text-neutral-600">
            No availability slots found. Drag on the calendar to create slots.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-(--neutral-white-NW1000) rounded-xl border border-neutral-200">
      <div className="px-6 py-4 border-b border-neutral-200">
        <h3 className="[font-size:var(--fs-h6)] font-semibold text-(--neutral-black-NB900)! m-0!">
          {title}
        </h3>
        <p className="text-sm text-neutral-600 m-0!">
          {availableSlots.length} available • {pendingSlots.length} pending • {bookedSlots.length}{' '}
          booked
        </p>
      </div>

      <div className="divide-y divide-neutral-200 max-h-100 overflow-y-auto">
        {slots.map((slot) => {
          const status = getSlotStatus(slot);
          const isPast = new Date(slot.end) < new Date();
          const student = slot.scheduledCall?.student;

          return (
            <div
              key={slot.id}
              className={cn(
                'p-4 hover:bg-neutral-50 transition-colors cursor-pointer',
                isPast && 'opacity-50'
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-neutral-500" />
                    <span className="text-sm font-medium text-neutral-900">
                      {formatSlotTime(slot.start, slot.end)}
                    </span>
                  </div>

                  {status === 'accepted' && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-(--secondary-B800)" />
                      <span className="[font-size:var(--fs-l5)] text-(--secondary-B800) font-bold">
                        Booked{student ? ` • ${student.firstName} ${student.lastName}` : ''}
                      </span>
                    </div>
                  )}

                  {status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-(--warning-W700)" />
                      <span className="[font-size:var(--fs-l5)] text-(--warning-W700) font-bold">
                        Pending{student ? ` • ${student.firstName} ${student.lastName}` : ''}
                      </span>
                    </div>
                  )}

                  {status === 'rejected' && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-(--danger-D700)" />
                      <span className="[font-size:var(--fs-l5)] text-(--danger-D700) font-bold">
                        Rejected{student ? ` • ${student.firstName} ${student.lastName}` : ''}
                      </span>
                    </div>
                  )}

                  {status === 'available' && (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-(--primary-O900)" />
                      <span className="[font-size:var(--fs-l5)] text-(--primary-O900) font-bold">
                        Available
                      </span>
                    </div>
                  )}
                </div>

                {status === 'available' && !isPast && (
                  <button
                    onClick={() => handleDelete(slot.id)}
                    disabled={deleteSlot.isPending}
                    className="p-2 text-neutral-400 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                    aria-label="Delete slot"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
